import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router/dom'
import { router } from './routes/router'
import { initI18n, reloadOnServiceWorkerUpdate, startOutbox } from '@waago/api'
import { AppErrorBoundary } from '@waago/ui'
import './index.css'

// A browser that has been here before is served the previous build from the
// service worker's cache. Registered first so the swap happens as early as
// possible on the visit after a deploy.
reloadOnServiceWorkerUpdate()

// Before the first render, so the app opens in the chosen language rather
// than flashing French and correcting itself.
initI18n()

// Replays anything queued while the network was gone. Started once here
// rather than per screen: a queued write must go out when the signal
// returns, whichever page the person happens to be looking at.
startOutbox()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Mobile data is expensive in this market: never retry a 4xx, and keep
      // retries low on anything else.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status
        if (status !== undefined && status >= 400 && status < 500) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
    },
  },
})

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root not found')

createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
)
